import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDrawerDispatch, useDrawerState } from 'context/DrawerContext'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { gql, useMutation, useQuery, useReactiveVar } from '@apollo/client'
import Button, { KIND, SHAPE } from 'components/Button/Button'
import {
  ButtonGroup,
  DrawerTitle,
  DrawerTitleWrapper,
  FieldDetails,
  Form,
  RowTitle,
} from 'containers/DrawerItems/DrawerItems.style'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { Col, Flex, Grid, Row } from 'components/FlexBox/FlexBox'
import DrawerBox from 'components/DrawerBox/DrawerBox'
import { FormFields, FormLabel } from 'components/FormFields/FormFields'
import Input, { SIZE } from 'components/Input/Input'
import { Label4 } from 'baseui/typography'
import { Scrollbars } from 'react-custom-scrollbars'
import { InLineLoader } from 'components/InlineLoader/InlineLoader'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { removeKey } from 'utils'
import { DURATION, SnackbarProvider, useSnackbar } from 'baseui/snackbar'
import { ArrowLeft, Check } from 'baseui/icon'
import Uploader from 'components/Uploader/Uploader'
import { SITE_SETTINGS } from 'settings/constants'
import { useHistory } from 'react-router-dom'
import { Notification, KIND as KINDNOTIFICATION } from 'baseui/notification'
import FontPicker from 'font-picker-react'
import { Textarea } from 'components/Textarea/Textarea'
import { useSettings, useUpdateSettings } from 'settings/useSettings'
import SettingField, { getSetting } from './SettingField'
import { Checkbox, LABEL_PLACEMENT, STYLE_TYPE } from 'baseui/checkbox'

export const CORE_DESIGN_SETTINGS_DATA = gql`
  fragment CoreDesignSettingsData on ShopSetting {
    id
    favicon {
      id
      src
    }
    theme_main_color
    theme_secondary_color
    font
    theme_override
    theme_styles_collection_block_simplified
  }
`

export const UPDATE_ABOUT_US_PAGE = gql`
  ${CORE_DESIGN_SETTINGS_DATA}
  mutation UPDATE_ABOUT_US_PAGE(
    $shopSettingsId: Int
    $favicon: [String]
    $theme_main_color: String
    $theme_secondary_color: String
    $font: String
    $theme_override: Json
    $theme_styles_collection_block_simplified: Boolean
  ) {
    updateDesignSettings(
      shopSettingsId: $shopSettingsId
      favicon: $favicon
      theme_main_color: $theme_main_color
      theme_secondary_color: $theme_secondary_color
      font: $font
      theme_override: $theme_override
      theme_styles_collection_block_simplified: $theme_styles_collection_block_simplified
    ) {
      ...CoreDesignSettingsData
    }
  }
`

// We only get the first shop settings, then we update it or create a new if its null.
export const GET_DESIGN_DATA = gql`
  ${CORE_DESIGN_SETTINGS_DATA}
  query GET_DESIGN_DATA {
    findFirstShopSetting {
      ...CoreDesignSettingsData
    }
  }
`

export const GET_SETTINGS = gql`
  query GET_SETTINGS {
    findManySetting {
      name
      title
      type
      value
    }
  }
`

// Validation rules....
const schema = yup.object().shape({
  // ----
  theme_override: yup
    .string()
    .test('is-json', 'Invalid JSON format', (value) => {
      try {
        JSON.parse(value)
        return true
      } catch (error) {
        return false
      }
    }),
})

const formatJSON = (json) => {
  return JSON.stringify(JSON.parse(json), null, 3)
}

type Props = any
const DesignSettingsForm: React.FC<Props> = () => {
  // const data = useDrawerState("data");
  // const id = data.postID;

  const { enqueue } = useSnackbar()
  const [font, setFont] = useState('Roboto')
  const [themeOverride, setThemeOverride] = useState('')
  const {
    settings,
    loading: settingsLoading,
    error: settingsError,
  } = useSettings()
  const { updateSettings: updateOtherSettings } = useUpdateSettings()

  //* ------------------------ DRAWER CONTEXT
  const dispatch = useDrawerDispatch()
  // ===== CLOSE DRAWER CALLBACK
  const closeDrawer = useCallback(
    () => dispatch({ type: 'CLOSE_DRAWER' }),
    [dispatch],
  )

  //* ==== Get all the posts data needed by passing the "data.postID" retrieved from Context
  const {
    data: { findFirstShopSetting: shopSettings } = {},
    loading,
    error,
    refetch,
    fetchMore,
  } = useQuery(GET_DESIGN_DATA)

  //* ==== Mutation to edita data in database
  const [
    updateSettings,
    {
      data: mutationData,
      called,
      loading: mutationLoading,
      error: mutationError,
    },
  ] = useMutation(UPDATE_ABOUT_US_PAGE, {
    onError: (e) => {
      // For mutation errors...
      console.log('Mutation error!', e)
      console.log('Extracted error!', e.graphQLErrors)

      enqueue(
        {
          message: 'Error!',
          startEnhancer: ({ size }) => <Check size={size} />,
          overrides: {
            Message: {
              style: ({ $theme }) => ({
                color: $theme.colors.red400,
              }),
            },
            StartEnhancerContainer: {
              style: ({ $theme }) => ({
                color: $theme.colors.red400,
              }),
            },
          },
        },
        DURATION.short,
      )
    },
    onCompleted: (e) => {
      // Handle the success case.
      enqueue(
        {
          message: 'Saving succesful!',
          startEnhancer: ({ size }) => <Check size={size} />,
          overrides: {
            Message: {
              style: ({ $theme }) => ({
                color: $theme.colors.lightGreen,
              }),
            },
            StartEnhancerContainer: {
              style: ({ $theme }) => ({
                color: $theme.colors.lightGreen,
              }),
            },
          },
        },
        DURATION.short,
      )
    },
    refetchQueries: ['GET_ABOUT_US_PAGE'],
  })

  //=========== REACT HOOOK FORM ============
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    control,
  } = useForm({
    resolver: yupResolver(schema),

    defaultValues: {
      shop_settings_id: null,
      favicon: [],
      theme_main_color: '',
      theme_secondary_color: '',
      theme_override: '{}',
      theme_styles_collection_block_simplified: false,
    },
  })

  const {
    control: controlOtherSettins,
    handleSubmit: handleSubmitOtherSettings,
  } = useForm()

  // =================================

  useEffect(() => {
    // Update Hex in state, once Graphql is finished
    if (shopSettings) {
      setValue('shop_settings_id', shopSettings.id ?? null)
      //------

      setValue(
        'favicon',
        shopSettings.favicon ? [shopSettings.favicon.src] : [],
      )
      setValue('theme_main_color', shopSettings.theme_main_color)
      setValue('theme_secondary_color', shopSettings.theme_secondary_color)

      setValue('theme_override', formatJSON(shopSettings.theme_override))

      setValue(
        'theme_styles_collection_block_simplified',
        shopSettings.theme_styles_collection_block_simplified ?? null,
      )
    }
  }, [setValue, shopSettings]) // We need to include called value (to refresh after mutation)

  const onSubmit = (data) => {
    console.log('Submitting -->', data)
    // Modify form data here =============

    // // Remove __typename keys from the query.
    const modifiedData = removeKey(data, '__typename')

    updateSettings({
      variables: {
        shopSettingsId: modifiedData.shop_settings_id,
        favicon: modifiedData.favicon,
        theme_main_color: modifiedData.theme_main_color,
        theme_secondary_color: modifiedData.theme_secondary_color,
        font,
        theme_override: formatJSON(modifiedData.theme_override),
        theme_styles_collection_block_simplified:
          modifiedData.theme_styles_collection_block_simplified,
      },
    })
  }

  const onSubmitOtherSettings = (data) => {
    console.log('Submitting -->', data)
    // Modify form data here =============

    // // Remove __typename keys from the query.
    const modifiedData = removeKey(data, '__typename')
    updateOtherSettings(modifiedData)
    // updateSettings({
    //   variables: {
    //     shopSettingsId: modifiedData.shop_settings_id,
    //     favicon: modifiedData.favicon,
    //     theme_main_color: modifiedData.theme_main_color,
    //     theme_secondary_color: modifiedData.theme_secondary_color,
    //     font,
    //     theme_override: formatJSON(modifiedData.theme_override),
    //   },
    // })
  }

  let history = useHistory()

  const myRef = useRef(null)
  const executeScroll = () => myRef.current.scrollIntoView()
  if (mutationError) {
    setTimeout(() => {
      executeScroll()
    }, 1000)
  }

  //* ===== always manage the loading state, or error will display "TypeError: Cannot read property 'productContainer' of undefined"
  if (settingsLoading) return <InLineLoader />
  if (settingsError) return <p>Query Error! {settingsError.message}</p>

  return (
    <>
      <Button
        // shape={SHAPE.circle}
        // startEnhancer={() => <ArrowLeft size={20} />}
        onClick={() => history.push(SITE_SETTINGS)}
        overrides={{
          BaseButton: {
            style: {
              padding: '0 10px',
              marginBottom: '10px',
            },
          },
        }}
      >
        <ArrowLeft size={40} />
      </Button>
      <br></br>
      <DrawerTitle>Design Settings</DrawerTitle>
      {Object.keys(errors).length > 0 ? (
        <Label4 color="red">There are ERRORS! (scroll up or down)</Label4>
      ) : null}

      <Form onSubmit={handleSubmit(onSubmit)}>
        {/* ============== Main image  ============== */}
        <Row>
          <Col lg={3} sm={12}>
            <DrawerBox sameHeight noBackground>
              <FormLabel>FAVICON</FormLabel>
              <FieldDetails>
                Upload your favicon (optional).<br></br>
                SIZE 64 x 64 px.
              </FieldDetails>
            </DrawerBox>
          </Col>
          <Col lg={9} sm={12}>
            <DrawerBox sameHeight>
              {mutationError && (
                <div ref={myRef}>
                  <Notification
                    kind={KINDNOTIFICATION.negative}
                    overrides={{
                      Body: { style: { width: 'auto' } },
                    }}
                  >
                    {mutationError.message}
                  </Notification>
                </div>
              )}
              <Controller
                name="favicon"
                control={control}
                rules={{
                  shouldUnregister: true,
                }}
                render={({ field }) => {
                  return (
                    <Uploader
                      onChange={field.onChange}
                      images={field.value}
                      multiple={false}
                    />
                  )
                }}
              />
            </DrawerBox>
          </Col>
        </Row>

        {/* ==============  DESIGN COLORS SECTION ============== */}
        <Row>
          <Col lg={3}>
            <DrawerBox sameHeight noBackground>
              <FormLabel>DESGIN COLORS (Advanced)</FormLabel>
              <FieldDetails>
                Here you can add some design tweaks for your block.
              </FieldDetails>
            </DrawerBox>
          </Col>
          <Col lg={9}>
            <DrawerBox
              overrides={{
                Block: {
                  style: {
                    width: '100%',
                    height: 'auto',
                    padding: '30px',
                    borderRadius: '3px',
                    backgroundColor: '#ffffff',
                  },
                },
              }}
            >
              {/* <Row>
                  <Col lg={9} sm={12}>
                    <FormLabel>Default Margins (optional)</FormLabel>
                    <FieldDetails style={{ padding: 0 }}>
                      You can have spaces between blocks or not. If turned On -
                      spaced will appear. If off blocks will touch each other.
                    </FieldDetails>
                  </Col>

                  <Col lg={3} sm={12}>
                    <FormFields>
                      <Controller
                        name="default_margins"
                        control={control}
                        rules={{
                          required: true,
                          shouldUnregister: true,
                        }}
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value}
                            checkmarkType={STYLE_TYPE.toggle_round}
                            onChange={field.onChange}
                            labelPlacement={LABEL_PLACEMENT.left}
                            overrides={{
                              Root: {
                                style: {
                                  float: 'right',
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </FormFields>
                  </Col>
                </Row> */}

              <Row>
                <Col lg={6} sm={12}>
                  <FormFields>
                    <FormLabel>theme_main_color (optional)</FormLabel>
                    <Input
                      type="text"
                      step={1}
                      {...register('theme_main_color', { required: false })}
                      error={errors.theme_main_color ? true : false}
                      placeholder="e.g.: #eeeeee"
                    />
                    {errors.theme_main_color && (
                      <Label4 color="red">
                        {errors.theme_main_color.message}
                      </Label4>
                    )}
                  </FormFields>
                </Col>
                <Col lg={6} sm={12}>
                  <FormFields>
                    <FormLabel>theme_secondary_color (optional)</FormLabel>
                    <Input
                      type="text"
                      step={1}
                      {...register('theme_secondary_color', {
                        required: false,
                      })}
                      error={errors.theme_secondary_color ? true : false}
                      placeholder="e.g.: #ffffff"
                    />
                    {errors.theme_secondary_color && (
                      <Label4 color="red">
                        {errors.theme_secondary_color.message}
                      </Label4>
                    )}
                  </FormFields>
                </Col>
              </Row>
            </DrawerBox>
          </Col>
        </Row>
        {/* ==============  FONTS SECTION ============== */}
        <Row>
          <Col lg={3}>
            <DrawerBox sameHeight noBackground>
              <FormLabel>MAIN FONTS</FormLabel>
              <FieldDetails>
                Selected font family will be used across the site.
              </FieldDetails>
            </DrawerBox>
          </Col>
          <Col lg={9}>
            <DrawerBox
              overrides={{
                Block: {
                  style: {
                    width: '100%',
                    height: 'auto',
                    padding: '30px',
                    borderRadius: '3px',
                    backgroundColor: '#ffffff',
                  },
                },
              }}
            >
              <Row>
                <Col lg={12} sm={12}>
                  <FormFields>
                    <FontPicker
                      apiKey={process.env.REACT_APP_FONTS_API_KEY}
                      activeFontFamily={font}
                      filter={(font) =>
                        font.variants.find((v) =>
                          v.includes('500') ? true : false,
                        )
                          ? font.variants.find((v) =>
                              v.includes('300') ? true : false,
                            )
                            ? true
                            : false
                          : false
                      }
                      onChange={(nextFont) => {
                        console.log('Font Changed!', nextFont)
                        setFont(nextFont.family)
                      }}
                      limit={100}
                    />
                    <p>
                      <span>Example:</span>
                      <span className="apply-font">
                        The quick brown fox jumps over the lazy dog. Lorem ipsum
                        dolor sit amet, consectetur adipiscing elit. Sed do
                        eiusmod tempor incididunt ut labore et dolore magna.
                        aliqua.
                      </span>
                    </p>
                  </FormFields>
                </Col>
              </Row>
            </DrawerBox>
          </Col>
        </Row>

        {/* ==============  Styles and Collections Block design ============== */}
        <Row>
          <Col lg={3} sm={12}>
            <DrawerBox sameHeight noBackground>
              <FormLabel>
                Styles / Collections Design block in product page
              </FormLabel>
            </DrawerBox>
          </Col>
          <Col lg={9} sm={12}>
            <DrawerBox sameHeight>
              <Row>
                <Col lg={9} sm={12}>
                  <FieldDetails style={{ padding: 0 }}>
                    Do we show a simplified version (True / On) or an extended
                    version (False / off). A simplified version will look the
                    same as in mobile.
                  </FieldDetails>
                </Col>

                <Col lg={3} sm={12}>
                  <FormFields>
                    <Controller
                      name="theme_styles_collection_block_simplified"
                      control={control}
                      rules={{
                        required: true,
                        shouldUnregister: true,
                      }}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          checkmarkType={STYLE_TYPE.toggle_round}
                          onChange={field.onChange}
                          labelPlacement={LABEL_PLACEMENT.left}
                          overrides={{
                            Root: {
                              style: {
                                float: 'right',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </FormFields>
                </Col>
              </Row>
            </DrawerBox>
          </Col>
        </Row>

        {/* ============== CUSTOM THEME SETTINGS ============== */}

        <Row>
          <Col lg={3}>
            <DrawerBox sameHeight noBackground>
              <FormLabel>CUSTOM THEME SETTINGS</FormLabel>
              <FieldDetails>Advanced settings for your theme.</FieldDetails>
            </DrawerBox>
          </Col>
          <Col lg={9}>
            <DrawerBox
              overrides={{
                Block: {
                  style: {
                    width: '100%',
                    height: 'auto',
                    padding: '30px',
                    borderRadius: '3px',
                    backgroundColor: '#ffffff',
                  },
                },
              }}
            >
              <Row>
                <Col lg={12} sm={12}>
                  <FormFields>
                    <FormLabel>Theme settings</FormLabel>
                    <Controller
                      name="theme_override"
                      control={control}
                      rules={{
                        required: false,
                        shouldUnregister: true,
                      }}
                      render={({ field }) => (
                        <Textarea
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={``}
                          size={SIZE.compact}
                          error={errors.theme_override ? true : false}
                          overrides={{
                            Input: {
                              style: ({ $theme }) => ({
                                height: '200px',
                                fontFamily: 'monospace',
                              }),
                            },
                          }}
                        />
                      )}
                    />
                  </FormFields>
                  {errors.theme_override && (
                    <Label4 color="red">{errors.theme_override.message}</Label4>
                  )}
                </Col>
              </Row>
            </DrawerBox>
          </Col>
        </Row>

        {/* ==============  SHOW CONTACT BLOCKS ============== */}
        {/* <Row>
          <Col lg={3} sm={12}>
            <DrawerBox sameHeight noBackground>
              <FormLabel>Contact Information</FormLabel>
            </DrawerBox>
          </Col>
          <Col lg={9} sm={12}>
            <DrawerBox sameHeight>
              <Row>
                <Col lg={9} sm={12}>
                  <FieldDetails style={{ padding: 0 }}>
                    You can choose, to enable or disable contact information in
                    the about us page.
                  </FieldDetails>
                </Col>

                <Col lg={3} sm={12}>
                  <FormFields>
                    <Controller
                      name="show_contact_blocks"
                      control={control}
                      rules={{
                        required: true,
                        shouldUnregister: true,
                      }}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          checkmarkType={STYLE_TYPE.toggle_round}
                          onChange={field.onChange}
                          labelPlacement={LABEL_PLACEMENT.left}
                          overrides={{
                            Root: {
                              style: {
                                float: 'right',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </FormFields>
                </Col>
              </Row>
            </DrawerBox>
          </Col>
        </Row> */}

        {/* ============== SUBMIT BUTTONS ============== */}

        <Button
          type="submit"
          disabled={mutationError}
          isLoading={mutationLoading}
          overrides={{
            BaseButton: {
              style: ({ $theme }) => ({
                width: '90%',
                margin: '15px 15px 15px 15px',
                // marginTop: "15px",
                // marginBottom: "15px",
                borderTopLeftRadius: '3px',
                borderTopRightRadius: '3px',
                borderBottomRightRadius: '3px',
                borderBottomLeftRadius: '3px',
              }),
            },
          }}
        >
          Save
        </Button>
      </Form>
      <Form onSubmit={handleSubmitOtherSettings(onSubmitOtherSettings)}>
        <Row>
          <Col lg={3} sm={12}>
            <DrawerBox sameHeight noBackground>
              <FormLabel>Product page </FormLabel>
              <FieldDetails>
                Various other design settings for product page<br></br>
              </FieldDetails>
            </DrawerBox>
          </Col>
          <Col lg={9} sm={12}>
            <DrawerBox sameHeight>
              <SettingField
                setting={getSetting(settings, 'use_sofa_configurator_v2')}
                control={controlOtherSettins}
              />
              <SettingField
                setting={getSetting(settings, 'use_fabric_selector_v2')}
                control={controlOtherSettins}
              />
              <SettingField
                setting={getSetting(settings, 'fabric_selector_v2_zoom')}
                control={controlOtherSettins}
              />
              <SettingField
                setting={getSetting(settings, 'show_more_products')}
                control={controlOtherSettins}
              />
              <SettingField
                setting={getSetting(settings, 'show_recently_viewed')}
                control={controlOtherSettins}
              />
              <SettingField
                setting={getSetting(settings, 'show_subscribe_to_newsletter')}
                control={controlOtherSettins}
              />
              <SettingField
                setting={getSetting(settings, 'collection_minimalistic_style')}
                control={controlOtherSettins}
              />
            </DrawerBox>
          </Col>
        </Row>
        <Button
          type="submit"
          disabled={mutationError}
          isLoading={mutationLoading}
          overrides={{
            BaseButton: {
              style: ({ $theme }) => ({
                width: '90%',
                margin: '15px 15px 15px 15px',
                // marginTop: "15px",
                // marginBottom: "15px",
                borderTopLeftRadius: '3px',
                borderTopRightRadius: '3px',
                borderBottomRightRadius: '3px',
                borderBottomLeftRadius: '3px',
              }),
            },
          }}
        >
          Update Other Settings
        </Button>
      </Form>
    </>
  )
}

export default DesignSettingsForm
